import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { triggerOptionsFor } from '../../lib/maintenanceTriggers';
import { useLocale } from '../../contexts/LocaleContext';
import {
  ScheduleFormFields,
  emptyScheduleForm,
  scheduleFormError,
  scheduleFormPayload,
  type ScheduleFormState,
} from './ScheduleFormFields';
import type { AssetKind, MaintenanceSchedule, MaintenanceTriggerType } from '../../types/equipment';

interface MaintenanceScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: string;
  assetKind: AssetKind | string | null | undefined;
  /** Null/undefined creates a new schedule; a schedule puts the dialog in edit mode. */
  schedule?: MaintenanceSchedule | null;
}

export default function MaintenanceScheduleDialog({
  open,
  onClose,
  equipmentId,
  assetKind,
  schedule,
}: MaintenanceScheduleDialogProps) {
  const queryClient = useQueryClient();
  const { isMetric } = useLocale();
  const isEdit = !!schedule;

  const options = useMemo(() => triggerOptionsFor(assetKind, isMetric), [assetKind, isMetric]);
  const [form, setForm] = useState<ScheduleFormState>(emptyScheduleForm());

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      setForm({
        name: schedule.name ?? '',
        trigger_type: schedule.triggerType,
        interval_value: schedule.intervalValue != null ? String(schedule.intervalValue) : '',
        last_performed_at: schedule.lastPerformedAt?.split(/[T ]/)[0] ?? '',
        last_performed_value:
          schedule.lastPerformedValue != null ? String(schedule.lastPerformedValue) : '',
      });
    } else {
      // Single-option asset kinds still require an explicit pick, so leave blank.
      setForm(emptyScheduleForm());
    }
  }, [open, schedule]);

  /**
   * An existing schedule can carry a trigger the picker does not offer — a
   * `meters_run` created via the API before this UI existed, or an odometer
   * unit that no longer matches the company's unit system. Lock rather than
   * coerce.
   */
  const lockedTrigger: MaintenanceTriggerType | null =
    isEdit && schedule && !options.some((o) => o.value === schedule.triggerType)
      ? schedule.triggerType
      : null;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = scheduleFormPayload(form);
      return isEdit
        ? api.equipmentMaintenanceSchedules.update(equipmentId, schedule!.id, payload)
        : api.equipmentMaintenanceSchedules.create(equipmentId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-schedules', equipmentId] });
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-due'] });
      toast.success(isEdit ? 'Schedule updated' : 'Schedule created');
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to save schedule');
    },
  });

  const handleSubmit = () => {
    const error = scheduleFormError(form);
    if (error) {
      toast.error(error);
      return;
    }
    saveMutation.mutate();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 overflow-y-auto py-8">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg mx-4 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
          <h3 className="text-lg font-bold text-white">
            {isEdit ? 'Edit Maintenance Schedule' : 'Add Maintenance Schedule'}
          </h3>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <ScheduleFormFields
            form={form}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            options={options}
            lockedTrigger={lockedTrigger}
          />
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-fieldiq-border">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-fieldiq-border text-fieldiq-muted rounded-lg text-sm font-medium hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2 rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
